import { useEffect, useState } from 'react';
import { Table } from '../components/Table';
import { PaginationState } from '@tanstack/react-table';
import {
  ColumnData,
  RequestOrderCancel,
  RequestOrderCancellationQuestion,
  RequestOrderCancelStates,
} from '../interface/interface';
import { useAuth } from 'react-oidc-context';
import {
  cancelOrderProduct,
  deniedCancel,
  getRequestOrders,
} from '../service/api';
import Loading from '../components/Loading';
import CustomModal from '../components/CustomModal';
import showToast from '../components/ToastComponent';
import TextAreaWithLimit from '../components/TextArea';

const RequestOrders = () => {
  const { user } = useAuth();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [search, setSearch] = useState<string>('');
  const [submitSearch, setSubmission] = useState<number>(0);
  const [data, setData] = useState([]);
  const [order, setOrder] = useState<RequestOrderCancel | undefined>(undefined);
  const [renderCounter, setRenderCounter] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [reason, setReason] = useState('');

  const requestOrders = () => {
    getRequestOrders(pagination, search, user?.access_token)
      .then((resp) => {
        setData(resp?.data?.data);
        setTotalRecords(resp?.data?.total);
        setOrder(undefined);
        setLoading(false);
      })
      .catch((err: any) => {
        console.error(err);
        setOrder(undefined);
        setLoading(false);
      });
  };

  const confirmDelete = (reason: string) => {
    if (!order) {
      console.error('Order id not found');
      return;
    }

    cancelOrderProduct(order.id, reason, user?.access_token)
      .then(() => {
        setRenderCounter((prev) => prev + 1);
        setIsModalOpen(false);
        showToast({
          message: 'Order cancellation request approved successfully',
          type: 'success',
        });
      })
      .catch((err) => {
        console.error(err);
        setIsModalOpen(false);
        showToast({
          message: 'Failed to cancel order. Please try again later',
          type: 'error',
        });
      });
  };

  const deniedRequest = (reason: string) => {
    if (!order) {
      console.error('Order id not found');
      return;
    }

    deniedCancel(order.id, reason, user?.access_token)
      .then(() => {
        setRenderCounter((prev) => prev + 1);
        setIsModalOpen(false);
        showToast({
          message: 'Order cancellation request rejected successfully',
          type: 'success',
        });
      })
      .catch((err) => {
        console.error(err);
        setIsModalOpen(false);
        showToast({
          message: 'Failed to deny. Please try again later',
          type: 'error',
        });
      });
  };

  useEffect(() => {
    if (!isModalOpen) {
      setIsModalOpen(false);
      setOrder(undefined);
      setReason('');
    }
  }, [isModalOpen]);

  const columns: Array<ColumnData> = [
    {
      key: 'orderInvoice',
      header: () => 'Order Invoice',
      cell: (info) => (info.getValue() ? info.getValue() : '-'),
      isSortable: true,
    },
    {
      key: 'patientName',
      header: () => 'Patient Name',
      cell: (info) => info.getValue(),
      isSortable: true,
    },
    {
      key: 'reason',
      header: () => 'Reason',
      cell: (info) => info.getValue(),
      isSortable: true,
    },
    {
      key: 'orderProductsCount',
      header: () => 'Ordered Products',
      cell: (info) => info.getValue(),
    },
    {
      key: 'orderStatus',
      header: () => 'Order Status',
      cell: (info) => info.getValue(),
    },
    {
      key: 'action',
      cell: (info) => (
        <div className="flex justify-around">
          <span
            className="text-green-500 cursor-pointer"
            onClick={() => {
              setOrder({
                id: info.row.original.orderId,
                reason: RequestOrderCancelStates.Approve,
              });
              setIsModalOpen(true);
            }}
          >
            Approve
          </span>
          <span
            className="text-red-500 cursor-pointer"
            onClick={() => {
              setOrder({
                id: info.row.original.orderId,
                reason: RequestOrderCancelStates.Deny,
              });
              setIsModalOpen(true);
            }}
          >
            Reject
          </span>
        </div>
      ),
    },
  ];

  useEffect(() => {
    setLoading(true);
    requestOrders();
  }, [pagination, submitSearch, renderCounter]);

  if (loading) {
    return <Loading />;
  }

  if (!data.length) {
    return <h4 className="text-center mt-5">No Request Raised Yet</h4>;
  }

  return (
    <>
      <div className="mx-5 my-5 p-5">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="sm:flex sm:items-center">
            <div className="sm:flex-auto">
              <h1 className="text-xl font-semibold leading-6 text-primary-blue-500">
                Request For Cancel Orders
              </h1>
            </div>
          </div>
          <div className="mt-8 flow-root">
            <div className="-mx-4 -my-2 overflow-x-hidden overflow-y-hidden sm:-mx-6 lg:-mx-8 min-h-[600px]">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <div className="overflow-hidden sm:rounded-lg">
                  <Table
                    columns={columns}
                    data={data}
                    enableSorting={true}
                    enablePagination={true}
                    enableSearch={true}
                    search={{
                      value: search,
                      changeHandler: setSearch,
                      submitSearch: setSubmission,
                    }}
                    pagintation={{
                      state: pagination,
                      setter: setPagination,
                      totalRecords,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CustomModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
        header={
          order?.reason === RequestOrderCancelStates.Approve
            ? RequestOrderCancellationQuestion.CANCEL_ORDER_QUESTION
            : RequestOrderCancellationQuestion.RESUME_ORDER_QUESTION
        }
        footer={
          <>
            <>
              <button
                className="mr-4 bg-primary-blue-500 text-white px-4 py-2 rounded"
                onClick={() => {
                  if (!reason?.trim()) {
                    showToast({
                      message: 'Please enter a reason before submitting.',
                      type: 'warning',
                    });
                    return;
                  }
                  if (order?.reason === RequestOrderCancelStates.Approve) {
                    confirmDelete(reason);
                  }

                  if (order?.reason === RequestOrderCancelStates.Deny) {
                    deniedRequest(reason);
                  }
                }}
              >
                {order?.reason === RequestOrderCancelStates.Approve
                  ? 'Submit'
                  : 'Yes'}
              </button>
              <button
                className="bg-white border border-solid border-primary-blue-500 text-primary-blue-500 px-4 py-2 rounded"
                onClick={() => {
                  setIsModalOpen(false);
                }}
              >
                {order?.reason === RequestOrderCancelStates.Approve
                  ? 'Cancel'
                  : 'No'}
              </button>
            </>
          </>
        }
        modalSize={'w-11/12 md:w-10/12 lg:w-4/12'}
      >
        <div className="flex flex-col gap-2">
          <label htmlFor="rejectReason" className="text-sm font-medium">
            Please enter a reason for{' '}
            {order?.reason === RequestOrderCancelStates.Approve
              ? 'approving'
              : 'rejecting'}{' '}
            this cancellation request:
          </label>
          <TextAreaWithLimit
            maxLength={256}
            text={reason}
            onTextChange={(text: string) => {
              setReason(text);
            }}
          />
        </div>
      </CustomModal>
    </>
  );
};

export default RequestOrders;
